import React, { useState } from "react"
import { Link } from "gatsby"

import {
  CalendarIcon,
  ChevronRightIcon,
  UsersIcon,
  UserGroupIcon,
  CollectionIcon,
  ClipboardListIcon,
  SparklesIcon,
  TagIcon,
  EyeIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  // TrophyIcon,
} from "@heroicons/react/outline"

import { unslugify, readableNumber } from "../../utils/text"
import { randomFromArray } from "../../utils/random"
import { setLogoImgFallbackOnError } from "../../utils/logoPlaceholder"

import Image from "../common/image"
import withLocation from "../common/withlocation"

import CommunityFeatureTags from "../feature/communityfeaturetags"
import CommunityStarRating from "./starrating"
import RankingTrophyIcon from "../common/rankingTrophyIcon"
import CommunityVerifiedBadge from "../common/communityVerifiedBadge"

// import "../styles/communitiestable.css"

const CommunitiesTable = ({
  communities,
  showPlatform,
  showTopics,
  linkModal,
  showCount,
  showVisitors,
  showRankings,
  queryParams,
  showPublicStats = true, // right now showing all the time, but just views (redirects hidden below)
  // splitByDates,
}) => {
  const [showAllCompact, setShowAllCompact] = useState(false)
  const richPageSize = showCount || 35
  const compactPageSize = 50
  const communitiesToShow = communities.slice(0, richPageSize)
  const compactCommunities = communities.slice(richPageSize)
  const compactToShow = showAllCompact
    ? compactCommunities
    : compactCommunities.slice(0, compactPageSize)
  const hasMoreCompact = compactCommunities.length > compactPageSize

  const showStats =
    showVisitors || (queryParams.stats && queryParams.stats === "1")

  return (
    <React.Fragment>
      <ul className="divide-y divide-gray-200 overflow-visible text-left">
        {communitiesToShow.map((c, index) => (
          <li key={c.slug} className="overflow-visible">
            <Link
              to={`/communities/${c.slug}/`}
              className="group block overflow-visible hover:bg-gray-50"
              state={{
                modal: linkModal || false,
                // nextCommunity: communities[index + 1],
              }}
            >
              <div className="flex items-center overflow-visible py-4 px-2 sm:px-6">
                <div className="min-w-0 flex-1 overflow-visible sm:flex sm:items-stretch">
                  <div className="relative flex flex-shrink-0 items-center self-stretch overflow-visible p-4 pl-0 sm:pl-1">
                    {showRankings && index < 10 ? (
                      <div className="absolute right-full  z-10 mr-3 hidden w-6 -translate-y-1/2 flex-col items-center gap-1 tabular-nums sm:flex">
                        <div className="w-full max-w-full rounded-full py-1 px-1 text-center text-lg font-bold leading-none text-gray-300 group-hover:bg-gray-50">
                          #{index + 1}
                        </div>
                        {index < 3 ? (
                          <RankingTrophyIcon
                            rank={index + 1}
                            className="h-6 w-6 ml-1"
                          />
                        ) : null}
                      </div>
                    ) : null}
                    {c.logo ? (
                      <img
                        className="h-20 w-20 object-contain rounded-md object-left sm:object-center"
                        src={c.logo}
                        width="70"
                        height="42"
                        loading="lazy"
                        alt={`${c.name} logo`}
                        onError={setLogoImgFallbackOnError}
                      />
                    ) : (
                      <Image
                        className="h-20 w-20 object-contain opacity-50 object-left sm:object-center"
                        fileName="icon.png"
                        alt="Community Placeholder"
                        width="70"
                        height="70"
                        objectFit="contain"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="mr-4">
                    <div className="flex text-sm items-center min-w-0 flex-wrap">
                      <h3 className="font-medium text-sm text-rose-500 truncate max-w-sm">
                        {c.name}
                      </h3>
                      {c.claimed ? (
                        <CommunityVerifiedBadge className="ml-0.5 h-4 w-4 flex-shrink-0 text-sky-500" />
                      ) : null}
                      {c.platform &&
                      showPlatform &&
                      c.platform !== "independent" ? (
                        <p className="ml-1 flex-shrink-0 font-normal text-gray-400">
                          {" "}
                          on {c.platform.split(",").join(" and ")}
                        </p>
                      ) : (
                        ""
                      )}
                      {c.showPromotedTag ? (
                        <span className="ml-2 text-xs bg-yellow-400 font-semibold text-white rounded-md px-2 py-0.5">
                          Featured
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                    {showTopics && c.topics && c.topics.length ? (
                      <div className="flex items-center align-start font-normal">
                        <p className="text-xs text-gray-400">
                          {c.topics
                            .split(",")
                            .map(t => unslugify(t))
                            .join(", ")}
                        </p>
                      </div>
                    ) : (
                      ""
                    )}

                    
                    {c.description ? (
                      <div className="mt-2 text-sm text-gray-600 line-clamp-4">
                        {c.description}
                      </div>
                    ) : (
                      ""
                    )}

                    {c.reviewCount !== null &&
                    c.reviewCount !== undefined &&
                    c.reviewCount !== "0" ? (
                      <div className="mt-2 flex items-center">
                        <CommunityStarRating community={c} starHeight={4} />
                      </div>
                    ) : (
                      ""
                    )}
                    {c.gold && c.reviewsPositive ? (
                      <div className="mt-2 text-sm text-gray-600">
                        <div className="line-clamp-2 text-gray-500 text-sm">
                          "{randomFromArray(c.reviewsPositive.split("||"))}"
                        </div>

                        {/*<div className="flex items-center">
                          {[1,2,3,4,5].map((s, index) => (
                            <svg key={s} xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 text-yellow-400 flex-shrink-0`} fill="#fef3c7" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                          ))}
                        </div>*/}
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                  <div className="block sm:inline mt-4 text-sm sm:w-48 text-gray-600 flex-shrink-0 sm:mt-0 sm:ml-auto space-y-1">
                    {c.members ? (
                      <div className="flex items-center">
                        <UserGroupIcon
                          className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-300"
                          aria-hidden="true"
                        />
                        <p>{readableNumber(c.members)} members</p>
                      </div>
                    ) : (
                      ""
                    )}

                    {/*{false && showTopics && c.topics && c.topics.length ? (
                      <div className="flex items-center align-start">
                        <ClipboardListIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-300" aria-hidden="true" />
                        <p>
                          {c.topics.split(',').map(t => unslugify(t)).join(', ')}
                        </p>
                      </div>
                    ) : ''}*/}

                    <div className="flex align-start text-xs">
                      <TagIcon
                        className="mt-0.5 flex-shrink-0 mr-1.5 h-5 w-5 text-gray-300"
                        aria-hidden="true"
                      />
                      <CommunityFeatureTags community={c} />
                    </div>
                    {showStats ? (
                      <div className="flex items-center space-x-2">
                        {c.sponsored || c.promoted ? (
                          <CurrencyDollarIcon
                            className="flex-shrink-0 h-5 w-5 text-yellow-400"
                            aria-hidden="true"
                          />
                        ) : c.gold ? (
                          <SparklesIcon
                            className="flex-shrink-0 h-5 w-5 text-yellow-400"
                            aria-hidden="true"
                          />
                        ) : (
                          <ChartBarIcon
                            className="flex-shrink-0 h-5 w-5 text-gray-300"
                            aria-hidden="true"
                          />
                        )}

                        <p className="text-xs text-gray-500">
                          {c.redirects} clicks
                        </p>
                        <p className="text-xs text-gray-500">
                          {c.visitors} visitors
                        </p>
                      </div>
                    ) : (
                      ""
                    )}
                    {showPublicStats && c.views ? (
                      <div className="flex items-center space-x-2">
                        <EyeIcon
                          className="flex-shrink-0 h-5 w-5 text-gray-300"
                          aria-hidden="true"
                        />

                        <p className="text-xs text-gray-500">
                          {c.views} views / month
                        </p>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                {/*<div className="ml-5 flex-shrink-0">
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>*/}
              </div>
            </Link>
          </li>
        ))}
      </ul>
      {compactCommunities.length ? (
        <nav
          className="mt-6 border-t border-gray-200 pt-5"
          aria-label="More communities in this list"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            More communities ({compactCommunities.length})
          </p>
          <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="w-full min-w-0 table-fixed text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="w-12 py-2 pl-3 pr-0 sm:w-12">
                    <span className="sr-only">Logo</span>
                  </th>
                  <th className="py-2 pl-2 pr-2 sm:pr-3">Community</th>
                  <th className="w-28 whitespace-nowrap py-2 pl-1 pr-2 text-right sm:w-32 sm:pr-3">
                    Members
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {compactToShow.map(c => {
                  const platformLabel =
                    showPlatform && c.platform && c.platform !== "independent"
                      ? c.platform
                          .split(",")
                          .map(s => unslugify(s.trim()))
                          .join(" and ")
                      : null
                  return (
                    <tr key={c.slug} className="hover:bg-gray-50">
                      <td className="align-top w-12 py-2 pl-3 pr-0">
                        {c.logo ? (
                          <img
                            className="h-9 w-9 object-contain rounded-md"
                            src={c.logo}
                            width="36"
                            height="36"
                            loading="lazy"
                            alt=""
                            onError={setLogoImgFallbackOnError}
                          />
                        ) : (
                          <Image
                            className="h-9 w-9 object-contain opacity-50"
                            fileName="icon.png"
                            alt=""
                            width="36"
                            height="36"
                            objectFit="contain"
                            loading="lazy"
                          />
                        )}
                      </td>
                      <td className="min-w-0 align-top py-2 pl-2 pr-2 sm:pr-3">
                        <div className="min-w-0">
                          <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-snug">
                            <Link
                              to={`/communities/${c.slug}/`}
                              className="font-medium text-rose-500 hover:underline"
                              state={{ modal: linkModal || false }}
                            >
                              {c.name}
                            </Link>
                            {platformLabel ? (
                              <span className="font-normal text-gray-500">
                                {" "}
                                on {platformLabel}
                              </span>
                            ) : null}
                          </div>
                          {c.description ? (
                            <p
                              className="mt-0.5 truncate text-xs leading-relaxed text-gray-600"
                              title={c.description}
                            >
                              {c.description}
                            </p>
                          ) : null}
                        </div>
                      </td>
                      <td className="align-top whitespace-nowrap py-2 pl-1 pr-2 text-right text-xs text-gray-600 sm:pr-3">
                        {c.members ? (
                          <span className="inline-flex items-center justify-end gap-1 tabular-nums">
                            <UserGroupIcon
                              className="h-4 w-4 flex-shrink-0 text-gray-300"
                              aria-hidden="true"
                            />
                            {readableNumber(c.members)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {hasMoreCompact && !showAllCompact ? (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => setShowAllCompact(true)}
                className="text-sm font-medium text-rose-500 hover:text-rose-600 hover:underline"
              >
                View more ({compactCommunities.length - compactPageSize})
              </button>
            </div>
          ) : null}
        </nav>
      ) : null}
      {communities.length === 0 ? (
        <div className="p-8 text-center text-gray-600 font-semibold opacity-75 h-96">
          No communities that meet these filters
        </div>
      ) : (
        ""
      )}
    </React.Fragment>
  )
}

export default withLocation(CommunitiesTable)
